import * as React from 'react';
import { TApiTaskDetailEntity } from '../../types/Tasks';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import RepeatIcon from '@mui/icons-material/Repeat';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { simpleDateFormat, simpleDateTimeFormat } from '../../Helpers';

interface ITaskDetailsTableProps {
  task: TApiTaskDetailEntity;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginTop: theme.spacing(3),
    },
    table: {
      width: '100%',
    },
    title: {
      flex: '1 1 100%',
      padding: theme.spacing(1, 2),
    },
    dueDateOverdue: {
      color: '#f44336',
    },
    scheduleHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      fontWeight: 600,
      padding: theme.spacing(1, 2),
    },
    scheduleDescription: {
      padding: theme.spacing(0, 2, 1.5),
      fontSize: '0.875rem',
    },
  })
);

const TaskDetailsTable: React.FC<ITaskDetailsTableProps> = ({
  task,
}): JSX.Element => {
  const classes = useStyles({});
  const { t } = useTranslation();

  const hasDetails =
    task.parentTitle ||
    task.budget ||
    task.startDate ||
    task.dueDate ||
    task.isRecurring;

  if (!hasDetails) {
    return <></>;
  }

  return (
    <Paper className={classes.root}>
      <TableContainer>
        <Typography className={classes.title} variant='h6' id='tableTitle'>
          {t('tasks.detail.other_details')}
        </Typography>
        <Table
          className={classes.table}
          size='small'
          aria-label='a dense table'
        >
          <TableBody>
            {task.parentTitle && (
              <TableRow>
                <TableCell component='th' scope='row'>
                  <Box fontWeight={'bold'}>{t('tasks.detail.parent')}</Box>
                </TableCell>
                <TableCell align='right'>{task.parentTitle}</TableCell>
              </TableRow>
            )}
            {task.budget && (
              <TableRow>
                <TableCell component='th' scope='row'>
                  <Box fontWeight={'bold'}>{t('tasks.detail.budget')}</Box>
                </TableCell>
                <TableCell align='right'>{task.budget}</TableCell>
              </TableRow>
            )}
            {task.startDate && (
              <TableRow>
                <TableCell component='th' scope='row'>
                  <Box fontWeight={'bold'}>{t('tasks.detail.start_date')}</Box>
                </TableCell>
                <TableCell align='right'>
                  {simpleDateFormat(new Date(task.startDate))}
                </TableCell>
              </TableRow>
            )}
            {task.dueDate && (
              <TableRow>
                <TableCell component='th' scope='row'>
                  <Box fontWeight={'bold'}>{t('tasks.detail.due_date')}</Box>
                </TableCell>
                <TableCell
                  align='right'
                  className={
                    new Date(task.dueDate) < new Date()
                      ? classes.dueDateOverdue
                      : undefined
                  }
                >
                  {simpleDateTimeFormat(new Date(task.dueDate))}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {task.isRecurring && task.scheduleDescription && (
          <Box id="task-schedule-section">
            <Typography className={classes.scheduleHeader}>
              <RepeatIcon style={{ fontSize: '1rem', color: '#0000008a' }} />
              {t('tasks.detail.schedule')}
            </Typography>
            <Typography className={classes.scheduleDescription}>
              {task.scheduleDescription}
            </Typography>
          </Box>
        )}
      </TableContainer>
    </Paper>
  );
};

export default TaskDetailsTable;
